import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../contexts/LanguageContext'
import {
  publishCV,
  updatePublicCV,
  unpublishCV,
  getPublicCVByCvId,
  checkSlugAvailability,
} from '../services/firebase/publicCv'
import type { CV } from '../types/cv'
import type { PublicCV } from '../types/publicCv'
import type { TemplateType } from '../utils/colorPalettes'

interface UsePublishCVReturn {
  publicCV: PublicCV | null
  isPublished: boolean
  isLoading: boolean
  isPublishing: boolean
  error: string | null
  publish: (cv: CV, template: TemplateType, colorPalette: string, slug?: string) => Promise<PublicCV | null>
  update: (cv: CV, template: TemplateType, colorPalette: string) => Promise<void>
  unpublish: () => Promise<void>
  checkSlug: (slug: string) => Promise<boolean>
}

export function usePublishCV(cvId: string | null): UsePublishCVReturn {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [publicCV, setPublicCV] = useState<PublicCV | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if this CV is already published
  useEffect(() => {
    if (!user || !cvId) {
      setPublicCV(null)
      return
    }

    let mounted = true
    setIsLoading(true)

    getPublicCVByCvId(user.uid, cvId)
      .then((result) => {
        if (mounted) {
          setPublicCV(result)
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (mounted) setIsLoading(false)
      })

    return () => { mounted = false }
  }, [user, cvId])

  const publish = useCallback(
    async (cv: CV, template: TemplateType, colorPalette: string, slug?: string): Promise<PublicCV | null> => {
      if (!user || !cvId) {
        setError(t.publish.loginToPublish)
        return null
      }

      setIsPublishing(true)
      setError(null)

      try {
        const result = await publishCV(user.uid, cvId, cv, template, colorPalette, slug)
        setPublicCV(result)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : t.publish.publishError)
        return null
      } finally {
        setIsPublishing(false)
      }
    },
    [user, cvId, t]
  )

  const update = useCallback(
    async (cv: CV, template: TemplateType, colorPalette: string) => {
      if (!publicCV) return
      setIsPublishing(true)
      setError(null)

      try {
        await updatePublicCV(publicCV.publicId, cv, template, colorPalette)
        setPublicCV({ ...publicCV, cv, template, colorPalette, updatedAt: new Date() })
      } catch (err) {
        setError(err instanceof Error ? err.message : t.publish.updateError)
      } finally {
        setIsPublishing(false)
      }
    },
    [publicCV, t]
  )

  const handleUnpublish = useCallback(async () => {
    if (!publicCV) return
    setIsPublishing(true)
    setError(null)

    try {
      await unpublishCV(publicCV.publicId)
      setPublicCV(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.publish.unpublishError)
    } finally {
      setIsPublishing(false)
    }
  }, [publicCV, t])

  const checkSlug = useCallback(async (slug: string): Promise<boolean> => {
    try {
      return await checkSlugAvailability(slug)
    } catch {
      return false
    }
  }, [])

  return {
    publicCV,
    isPublished: publicCV !== null,
    isLoading,
    isPublishing,
    error,
    publish,
    update,
    unpublish: handleUnpublish,
    checkSlug,
  }
}
